import React, { useState, useEffect } from 'react';
import { batchAPI } from '../services/api';

const BatchFolderView = ({ batchId, userRole }) => {
  const [folderData, setFolderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingFile, setViewingFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [metadataValidation, setMetadataValidation] = useState(null);
  const [revalidating, setRevalidating] = useState(false);

  useEffect(() => {
    fetchFolderStructure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const fetchFolderStructure = async () => {
    try {
      const response = await batchAPI.getFolderStructure(batchId);
      setFolderData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching folder structure:', error);
      setLoading(false);
    }
  };

  const handleViewFile = async (filename) => {
    setViewingFile(filename);
    setLoadingFile(true);
    setFileContent(null);
    setIsEditing(false);
    setEditedData(null);
    setHasChanges(false);
    setMetadataValidation(null);

    try {
      const response = await batchAPI.getFile(batchId, filename);
      setFileContent(response.data);
      setEditedData(JSON.parse(JSON.stringify(response.data))); // Deep copy

      // Fetch metadata validation status if viewing GroupMetadataImage file
      if (filename.toLowerCase().includes('groupmetadataimage')) {
        try {
          const validationResponse = await batchAPI.getMetadataValidationStatus(batchId);
          if (validationResponse.data.validation_available) {
            setMetadataValidation(validationResponse.data.validation_results);
          }
        } catch (err) {
          console.error('Error fetching metadata validation:', err);
        }
      }
    } catch (error) {
      console.error('Error loading file:', error);
      alert('Failed to load file');
      setViewingFile(null);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleEnableEdit = () => {
    setIsEditing(true);
  };

  const handleCellEdit = (rowIndex, header, value) => {
    const newData = { ...editedData };
    newData.data[rowIndex][header] = value;
    setEditedData(newData);

    // Check if there are any changes
    const hasChanged = JSON.stringify(newData) !== JSON.stringify(fileContent);
    setHasChanges(hasChanged);
  };

  const handleResubmit = async () => {
    if (!hasChanges) {
      alert('No changes detected');
      return;
    }

    const confirmSubmit = window.confirm(
      'Are you sure you want to update the GroupMetadata file?\n\n' +
      'This will submit the batch for rework review and the auditor will need to review it again.'
    );
    if (!confirmSubmit) return;

    setSubmitting(true);

    // Check if this is a GroupMetadata file
    const isGroupMetadata = viewingFile.toLowerCase().includes('groupmetadataimage');

    try {
      // Show revalidation status for GroupMetadata files
      if (isGroupMetadata) {
        setRevalidating(true);
      }

      const response = await batchAPI.updateGroupMetadata(batchId, viewingFile, editedData);

      // For GroupMetadata files, fetch updated validation status
      if (isGroupMetadata) {
        try {
          const validationResponse = await batchAPI.getMetadataValidationStatus(batchId);
          if (validationResponse.data.validation_available) {
            setMetadataValidation(validationResponse.data.validation_results);
          }
        } catch (err) {
          console.error('Error fetching updated validation:', err);
        }
        setRevalidating(false);
      }

      alert(
        'GroupMetadata file updated successfully!\n\n' +
        'Your batch has been submitted for rework review. The auditor will review your changes.' +
        (response.data.validation_triggered ? '\n\nValidation completed and colors updated.' : '')
      );
      setFileContent(editedData);
      setIsEditing(false);
      setHasChanges(false);

      // Refresh folder structure to update validation colors
      fetchFolderStructure();
    } catch (error) {
      console.error('Error updating metadata:', error);
      alert(`Failed to update file: ${error.response?.data?.detail || error.message}`);
      setRevalidating(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadArtifactCSV = () => {
    if (!fileContent) return;
    const headers = fileContent.headers.join(',');
    const rows = fileContent.data.map(row => fileContent.headers.map(h => row[h] ?? '').join(','));
    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ArtifactMetadata.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    if (hasChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    setViewingFile(null);
    setIsEditing(false);
    setEditedData(null);
    setHasChanges(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getHeaderStyles = (header) => {
    // Skip coloring for ArtifactMetadata files (they are auto-generated and always correct)
    if (viewingFile && viewingFile.toLowerCase().includes('artifactmetadata')) {
      return { background: '#f8fafc', color: '#1e293b' };
    }

    // Check for Total Artifacts column (local validation, not AI)
    const totalArtifactsHeaders = ['Total Artifacts', 'Total_Artifacts', 'TotalArtifacts'];
    const isTotalArtifactsColumn = totalArtifactsHeaders.some(h =>
      header.toLowerCase().replace(/\s+/g, '') === h.toLowerCase().replace(/\s+/g, '')
    );

    if (isTotalArtifactsColumn && folderData) {
      // Check if artifact count validation was performed
      if (folderData.artifact_count_match === true) {
        return {
          background: '#16a34a', // Dark green (green-600)
          color: '#ffffff', // White text
        };
      } else if (folderData.artifact_count_match === false) {
        return {
          background: '#dc2626', // Dark red (red-600)
          color: '#ffffff', // White text
        };
      }
    }

    // Check for Capture ID column (local validation, not AI)
    const captureIdHeaders = ['Capture ID', 'Capture_ID', 'CaptureID'];
    const isCaptureIdColumn = captureIdHeaders.some(h =>
      header.toLowerCase().replace(/\s+/g, '') === h.toLowerCase().replace(/\s+/g, '')
    );

    if (isCaptureIdColumn && folderData) {
      // Check if capture ID validation was performed
      if (folderData.capture_id_match === true) {
        return {
          background: '#16a34a', // Dark green (green-600)
          color: '#ffffff', // White text
        };
      } else if (folderData.capture_id_match === false) {
        return {
          background: '#dc2626', // Dark red (red-600)
          color: '#ffffff', // White text
        };
      }
    }

    // Check if this header is one of the 4 AI-validated fields
    if (!metadataValidation) {
      return { background: '#f8fafc', color: '#1e293b' };
    }

    const validatedFields = ['Title', 'Start Date', 'End Date', 'Record Type'];

    if (validatedFields.includes(header)) {
      const value = metadataValidation[header];

      // If value is "T", return dark green with white text
      if (value === 'T') {
        return {
          background: '#16a34a', // Dark green (green-600)
          color: '#ffffff', // White text
        };
      } else {
        // If value is NOT "T", return dark red with white text
        return {
          background: '#dc2626', // Dark red (red-600)
          color: '#ffffff', // White text
        };
      }
    }

    // Default background for other headers
    return { background: '#f8fafc', color: '#1e293b' };
  };

  const styles = {
    container: {
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '24px',
    },
    header: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    fileList: {
      marginBottom: '24px',
    },
    fileItem: {
      padding: '12px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    fileName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    fileSize: {
      fontSize: '12px',
      color: '#64748b',
    },
    artifactsFolder: {
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      overflow: 'hidden',
      transition: 'all 0.2s',
    },
    folderHeader: {
      padding: '12px 16px',
      background: '#f8fafc',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'background 0.2s',
    },
    folderTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    imageGrid: {
      padding: '16px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '12px',
      maxHeight: '400px',
      overflowY: 'auto',
    },
    imageCard: {
      padding: '12px',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      textAlign: 'center',
      background: 'white',
      transition: 'all 0.2s',
    },
    imageName: {
      fontSize: '12px',
      color: '#1e293b',
      fontWeight: '500',
      marginBottom: '4px',
      wordBreak: 'break-all',
    },
    imageSize: {
      fontSize: '11px',
      color: '#64748b',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modalContent: {
      background: 'white',
      borderRadius: '8px',
      maxWidth: '900px',
      width: '100%',
      maxHeight: '80vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    modalHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    modalTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
    },
    closeButton: {
      padding: '6px 12px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
    },
    editButton: {
      padding: '6px 12px',
      background: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      marginRight: '8px',
    },
    resubmitButton: {
      padding: '6px 12px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      marginRight: '8px',
    },
    resubmitButtonDisabled: {
      padding: '6px 12px',
      background: '#94a3b8',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'not-allowed',
      fontSize: '13px',
      fontWeight: '500',
      marginRight: '8px',
    },
    modalBody: {
      padding: '24px',
      overflowY: 'auto',
      flex: 1,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
    },
    th: {
      padding: '10px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      textAlign: 'left',
      fontWeight: '600',
      color: '#1e293b',
    },
    td: {
      padding: '10px',
      border: '1px solid #e2e8f0',
      color: '#334155',
    },
    editableInput: {
      width: '100%',
      padding: '4px 8px',
      border: '1px solid #3b82f6',
      borderRadius: '4px',
      fontSize: '13px',
      outline: 'none',
      background: '#eff6ff',
    },
  };

  if (loading) {
    return <div style={styles.container}>Loading folder structure...</div>;
  }

  if (!folderData) {
    return <div style={styles.container}>No folder data available</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span>📁</span>
        <span>Batch Folder: {folderData.batch_name}</span>
      </div>

      {/* Files Section */}
      {folderData.files && folderData.files.length > 0 && (
        <div style={styles.fileList}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '12px', textTransform: 'uppercase' }}>
            Files
          </h3>
          {folderData.files.map((file, index) => (
            <div
              key={index}
              style={styles.fileItem}
              onClick={() => handleViewFile(file.name)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#10b981';
                e.currentTarget.style.background = '#f0fdf4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = 'white';
              }}
            >
              <div style={styles.fileName}>
                <span>{file.type === 'csv' ? '📄' : file.type === 'excel' ? '📊' : '📎'}</span>
                <span>{file.name}</span>
                {file.validation_status && (
                  <span style={{
                    marginLeft: '12px',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: file.validation_status === 'PASS' ? '#dcfce7' : '#fee2e2',
                    color: file.validation_status === 'PASS' ? '#16a34a' : '#dc2626'
                  }}>
                    {file.validation_status}
                  </span>
                )}
              </div>
              <div style={styles.fileSize}>{formatFileSize(file.size)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Artifacts Folder */}
      {folderData.artifacts_folder && (
        <div style={styles.artifactsFolder}>
          <div
            style={styles.folderHeader}
            onClick={() => setShowImages(!showImages)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8fafc';
            }}
          >
            <div style={styles.folderTitle}>
              <span>📁</span>
              <span>{folderData.artifacts_folder}/</span>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                ({folderData.artifacts_images.length} images)
              </span>
            </div>
            {!showImages && (
              <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500', cursor: 'pointer' }}>
                Click to view images →
              </span>
            )}
          </div>

          {showImages && (
            <div style={styles.imageGrid}>
              {folderData.artifacts_images.map((image, index) => (
                <div
                  key={index}
                  style={styles.imageCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
                  <div style={styles.imageName}>{image.name}</div>
                  <div style={styles.imageSize}>{formatFileSize(image.size)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File Viewer Modal */}
      {viewingFile && (
        <div style={styles.modal} onClick={handleCloseModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>{viewingFile}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {viewingFile && viewingFile.toLowerCase().includes('artifactmetadata') && fileContent && (
                  <button
                    onClick={handleDownloadArtifactCSV}
                    style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                    title="Download CSV"
                  >
                    ⬇ Download
                  </button>
                )}
                {!isEditing && userRole === 'vendor_user' && viewingFile.toLowerCase().includes('groupmetadata') && (
                  <button
                    onClick={handleEnableEdit}
                    style={styles.editButton}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d97706'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#f59e0b'}
                  >
                    Edit
                  </button>
                )}
                {isEditing && (
                  <button
                    onClick={handleResubmit}
                    style={hasChanges ? styles.resubmitButton : styles.resubmitButtonDisabled}
                    disabled={!hasChanges || submitting}
                    onMouseEnter={(e) => {
                      if (hasChanges && !submitting) {
                        e.currentTarget.style.background = '#059669';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasChanges && !submitting) {
                        e.currentTarget.style.background = '#10b981';
                      }
                    }}
                  >
                    {submitting ? 'Updating...' : 'Resubmit'}
                  </button>
                )}
                <button
                  onClick={handleCloseModal}
                  style={styles.closeButton}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={styles.modalBody}>
              {loadingFile && <div>Loading file...</div>}
              {revalidating && (
                <div style={{
                  padding: '16px',
                  marginBottom: '16px',
                  background: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#92400e'
                }}>
                  🔄 Group metadata Excel is revalidating... Please wait.
                </div>
              )}
              {fileContent && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {fileContent.headers?.map((header, index) => {
                          const headerStyles = getHeaderStyles(header);
                          return (
                            <th
                              key={index}
                              style={{
                                ...styles.th,
                                background: headerStyles.background,
                                color: headerStyles.color
                              }}
                            >
                              {header}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {editedData && editedData.data?.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {editedData.headers?.map((header, colIndex) => (
                            <td key={colIndex} style={styles.td}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={row[header] || ''}
                                  onChange={(e) => handleCellEdit(rowIndex, header, e.target.value)}
                                  style={styles.editableInput}
                                />
                              ) : (
                                row[header]
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchFolderView;
